import { useEffect, useRef, useState } from "react";

export const cn = (...classes) => classes.filter(Boolean).join(" ");

export const PRODUCT_LOGOS = {
  RentDirect: { src: "/logos/products/rentdirect-horizontal.png", width: 732, height: 267 },
  RentShield: { src: "/logos/products/rentshield-horizontal.png", width: 465, height: 213 },
  RentID: { src: "/logos/products/rentid-horizontal.png", width: 1116, height: 636 },
  RentSuccess: { src: "/logos/products/rentsuccess-horizontal.png", width: 739, height: 268 },
};

/* Icon-only product marks (get-started page). */
export const PRODUCT_MARKS = {
  RentShield: { src: "/images/rentid-suite/rentshield-mark.png", width: 191, height: 221 },
  RentID: { src: "/images/rentid-suite/rentid-mark.png", width: 192, height: 216 },
  RentSuccess: { src: "/images/rentid-suite/rentsuccess-mark.png", width: 220, height: 220 },
  RentDirect: { src: "/images/rentid-suite/rentdirect-mark.png", width: 199, height: 188 },
};

export function ProductLogo({ name, className }) {
  const logo = PRODUCT_LOGOS[name];
  return (
    <img
      src={logo.src}
      alt={name}
      width={logo.width}
      height={logo.height}
      className={cn("object-contain object-left", className || "h-10 w-auto")}
    />
  );
}

/* Sets `inView` once the element first scrolls into view. */
export function useInView(options) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ref, inView };
}

export function Reveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ rootMargin: "0px 0px -10% 0px" });
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        "transition-all duration-700 ease-out motion-reduce:transition-none",
        inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* Closes a popover on outside click or Escape (stands in for the Radix primitives v0 uses). */
export function useDismiss(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose]);
  return ref;
}
